import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Avatar,
  Badge,
  Progress,
  IconButton,
  Tooltip,
  useToast,
  useColorModeValue,
  Container,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Divider,
  AspectRatio,
  Image,
  useBreakpointValue,
  Spinner,
  Center,
  Icon,
  Skeleton,
  SkeletonText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Select,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider
} from '@chakra-ui/react';
import {
  FiPlay,
  FiPause,
  FiVolume2,
  FiUser,
  FiMaximize,
  FiClock,
  FiUsers,
  FiStar,
  FiCheckCircle,
  FiLock,
  FiUnlock,
  FiVideo,
  FiFileText,
  FiMessageSquare,
  FiThumbsUp,
  FiThumbsDown,
  FiMoreVertical,
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiArrowLeft,
  FiArrowRight,
  FiEye,
  FiCalendar,
  FiAward,
  FiTarget,
  FiTrendingUp,
  FiBook,
  FiHeadphones,
  FiMonitor,
  FiSmartphone,
  FiWifi,
  FiGlobe,
  FiChevronDown,
  FiChevronUp,
  FiSend
} from 'react-icons/fi';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { fetchCourseById } from '../services/courseService';
import VideoPlayer from '../shared/videoplayer';
import axiosInstance from '../services/axiosInstance';
import { getUserComments, getCommentsWithUserFilter } from '../services/userCommentService';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import AuthContext from '../services/AuthContext';
import useTrackSession from '../utils/useTrackSession';
import SidebarInstructor from './../shared/Sidebar3';

// Enhanced Animation variants with more sophisticated effects
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Enhanced color scheme with gradients and modern colors
const getChapterColorScheme = () => {
  return {
    chapter: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    chapterSolid: '#667eea',
    part: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    partSolid: '#f093fb',
    video: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    videoSolid: '#4facfe',
    text: '#2D3748',
    accent: '#805AD5'
  };
};

// Enhanced Comment status badge component with better styling
const CommentStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: {
      color: 'orange',
      text: 'Pending',
      bg: 'linear-gradient(135deg, #ff9a56 0%, #ffad56 100%)'
    },
    approved: {
      color: 'green',
      text: 'Approved',
      bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    replied: {
      color: 'blue',
      text: 'Replied',
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    hidden: {
      color: 'red',
      text: 'Hidden',
      bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    public: {
      color: 'teal',
      text: 'Public',
      bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <></>
  );
};

// Enhanced Reply component with better visual hierarchy
const CommentReply = ({ reply, isInstructor }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        ml={8}
        mt={3}
        p={4}
        bg="linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(247,250,252,0.9) 100%)"
        _dark={{ bg: "linear-gradient(135deg, rgba(45,55,72,0.8) 0%, rgba(74,85,104,0.9) 100%)" }}
        borderRadius="xl"
        borderLeft="4px solid"
        borderLeftColor={reply.isInstructor ? "purple.400" : "blue.300"}
        boxShadow="0 4px 12px rgba(0,0,0,0.05)"
        backdropFilter="blur(10px)"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'xl',
          padding: '1px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.1))',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude'
        }}
      >
        <HStack spacing={3} align="center" mb={3}>
          <Avatar
            size="xs"
            name={reply.sender}
            border="2px solid"
            borderColor={reply.isInstructor ? "purple.300" : "blue.200"}
          />
          <Text fontSize="13px" fontWeight="600" color="gray.900" _dark={{ color: "gray.100" }}>
            @{reply.sender}
          </Text>
          {reply.isInstructor && (
            <Badge
              colorScheme="purple"
              size="xs"
              borderRadius="full"
              px={2}
              fontWeight="600"
            >
              Instructor
            </Badge>
          )}
          <Text fontSize="11px" color="gray.500" ml="auto">
            {new Date(reply.time).toLocaleDateString()}
          </Text>
        </HStack>
        <Text fontSize="14px" lineHeight="1.6" color="gray.800" _dark={{ color: "gray.200" }}>
          {reply.message}
        </Text>
      </Box>
    </motion.div>
  );
};

// Enhanced YouTube-style Comments Component
const YouTubeComments = ({
  comments,
  onCommentSubmit,
  comment,
  setComment,
  user,
  courseData,
  selectedVideo,
  onReplySubmit
}) => {
  const [sortBy, setSortBy] = useState('newest');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Check if user is instructor
  const isInstructor = user?.role === 'instructor' ||
    user?.username === courseData?.teacherName ||
    user?._id === courseData?.teacherId;

  // Filter comments based on backend schema logic
  const getVisibleComments = () => {
    if (!user || !courseData) return [];
    console.log(user)
    console.log(comments)
    return comments.filter(commentItem => {
      // Instructors can see all comments
      if (isInstructor) return true;

      // Students can only see:
      // 1. Their own comments
      // 2. Public/approved comments

      const isOwnComment = commentItem.userId === user._id || commentItem.sender === user.username;
      const isPublicComment = ['approved', 'public'].includes(commentItem.status);

      return isOwnComment || isPublicComment;
    });
  };

  const visibleComments = getVisibleComments();

  const handleReplySubmit = async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await onReplySubmit(commentId, replyText);
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <Box
        mt={8}
        bg="linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(247,250,252,0.95) 100%)"
        _dark={{
          bg: "linear-gradient(135deg, rgba(26,32,44,0.9) 0%, rgba(45,55,72,0.95) 100%)",
          borderColor: "rgba(255,255,255,0.1)"
        }}
        borderRadius="2xl"
        p={6}
        boxShadow="0 10px 25px rgba(0,0,0,0.1)"
        backdropFilter="blur(20px)"
        border="1px solid"
        borderColor="rgba(255,255,255,0.2)"
      >
        <VStack align="stretch" spacing={8}>
          {/* Enhanced Comments Header */}
          <motion.div variants={fadeIn}>
            <HStack justify="space-between" align="center">
              <HStack spacing={4}>
                <Heading size="lg" fontWeight="700" bgGradient="linear(to-r, purple.400, blue.400)" bgClip="text">
                  {visibleComments.length} Comments
                </Heading>
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="sm"
                    rightIcon={<FiChevronDown />}
                    fontWeight="600"
                    borderRadius="full"
                    _hover={{
                      bg: "linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                      transform: "translateY(-1px)"
                    }}
                    _dark={{ _hover: { bg: "rgba(255,255,255,0.1)" } }}
                    transition="all 0.2s ease"
                  >
                    Sort by
                  </MenuButton>
                  <MenuList
                    borderRadius="xl"
                    boxShadow="0 10px 25px rgba(0,0,0,0.15)"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.2)"
                  >
                    <MenuItem
                      onClick={() => setSortBy('newest')}
                      borderRadius="lg"
                      _hover={{ bg: "purple.50" }}
                    >
                      Newest first
                    </MenuItem>
                    <MenuItem
                      onClick={() => setSortBy('oldest')}
                      borderRadius="lg"
                      _hover={{ bg: "purple.50" }}
                    >
                      Oldest first
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </HStack>
          </motion.div>

          {/* Enhanced Add Comment Section */}
          <motion.div variants={slideIn}>
            <HStack align="start" spacing={4}>
              <Avatar
                size="md"
                src={user?.avatar || "/assets/user-avatar.jpg"}
                name={user?.username}
                border="3px solid"
                borderColor="purple.200"
                _dark={{ borderColor: "purple.400" }}
              />
              <VStack align="stretch" spacing={4} flex="1">
                <Textarea
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  border="2px solid transparent"
                  borderRadius="xl"
                  bg="rgba(255,255,255,0.8)"
                  _dark={{ bg: "rgba(45,55,72,0.8)" }}
                  resize="none"
                  minH="60px"
                  fontSize="15px"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 3px rgba(128,90,213,0.1)",
                    bg: "white",
                    _dark: { bg: "gray.700" }
                  }}
                  _hover={{
                    borderColor: "purple.200",
                    _dark: { borderColor: "purple.300" }
                  }}
                  transition="all 0.2s ease"
                />
                {comment && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HStack justify="flex-end" spacing={3}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setComment('')}
                        borderRadius="full"
                        fontSize="14px"
                        fontWeight="600"
                        px={6}
                        _hover={{
                          bg: "gray.100",
                          transform: "translateY(-1px)"
                        }}
                        _dark={{ _hover: { bg: "gray.700" } }}
                        transition="all 0.2s ease"
                      >
                        Cancel
                      </Button>
                      <Button
                        bgGradient="linear(to-r, purple.400, blue.400)"
                        color="white"
                        size="sm"
                        _hover={{
                          bgGradient: "linear(to-r, purple.500, blue.500)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(128,90,213,0.3)"
                        }}
                        onClick={onCommentSubmit}
                        isDisabled={!comment.trim()}
                        borderRadius="full"
                        fontSize="14px"
                        fontWeight="600"
                        px={8}
                        transition="all 0.2s ease"
                      >
                        Comment
                      </Button>
                    </HStack>
                  </motion.div>
                )}
              </VStack>
            </HStack>
          </motion.div>

          {/* Enhanced Comments List */}
          <motion.div variants={staggerContainer}>
            <VStack align="stretch" spacing={6}>
              {visibleComments.length === 0 ? (
                <motion.div variants={fadeIn}>
                  <Center py={12}>
                    <VStack spacing={4}>
                      <Box
                        p={4}
                        borderRadius="full"
                        bg="linear-gradient(135deg, rgba(128,90,213,0.1) 0%, rgba(102,126,234,0.1) 100%)"
                      >
                        <Icon as={FiMessageSquare} boxSize={10} color="purple.400" />
                      </Box>
                      <Text color="gray.500" fontSize="lg" fontWeight="500">
                        No comments to display
                      </Text>
                    </VStack>
                  </Center>
                </motion.div>
              ) : (
                visibleComments.map((commentItem, index) => {
                  const isOwnComment = commentItem.user === user?._id || commentItem.sender === user?.username;
                  const commentIsInstructor = commentItem.sender === courseData?.teacherName;

                  return (
                    <motion.div
                      key={commentItem._id}
                      variants={fadeIn}
                      custom={index}
                    >
                      <Box
                        p={5}
                        bg="rgba(255,255,255,0.7)"
                        _dark={{ bg: "rgba(45,55,72,0.7)", borderColor: "rgba(255,255,255,0.1)" }}
                        borderRadius="xl"
                        boxShadow="0 4px 12px rgba(0,0,0,0.05)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.3)"
                        _hover={{
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                        }}
                        transition="all 0.3s ease"
                      >
                        <HStack align="start" spacing={4}>
                          <Avatar
                            size="md"
                            name={commentItem.user?.name || commentItem.sender}
                            src={commentItem.user?.avatar || commentItem.avatar}
                            border="2px solid"
                            borderColor={commentIsInstructor ? "purple.300" : "blue.200"}
                          />
                          <VStack align="stretch" spacing={3} flex="1">
                            <HStack spacing={3} align="center" flexWrap="wrap">
                              <Text fontSize="15px" fontWeight="700" color="gray.900" _dark={{ color: "gray.100" }}>
                                @{commentItem.user?.name || commentItem.sender}
                              </Text>
                              {commentIsInstructor && (
                                <Badge
                                  bgGradient="linear(to-r, purple.400, blue.400)"
                                  color="white"
                                  size="sm"
                                  borderRadius="full"
                                  px={3}
                                  fontWeight="600"
                                >
                                  Instructor
                                </Badge>
                              )}

                              <CommentStatusBadge status={commentItem.status} />
                              <Text fontSize="13px" color="gray.500" ml="auto">
                                {new Date(commentItem.createdAt).toLocaleDateString()}
                              </Text>
                            </HStack>

                            <Text fontSize="15px" lineHeight="1.6" color="gray.900" _dark={{ color: "gray.100" }}>
                              {commentItem.message}
                            </Text>

                            <HStack spacing={6} mt={2}>
                              <HStack
                                as="button"
                                spacing={2}
                                _hover={{
                                  color: "purple.500",
                                  transform: "scale(1.05)"
                                }}
                                transition="all 0.2s ease"
                                p={2}
                                borderRadius="lg"
                              >
                                <Icon as={FiThumbsUp} boxSize={4} />
                                <Text fontSize="13px" fontWeight="600">0</Text>
                              </HStack>
                              <HStack
                                as="button"
                                spacing={2}
                                _hover={{
                                  color: "red.400",
                                  transform: "scale(1.05)"
                                }}
                                transition="all 0.2s ease"
                                p={2}
                                borderRadius="lg"
                              >
                                <Icon as={FiThumbsDown} boxSize={4} />
                              </HStack>

                              {/* Enhanced Reply button */}
                              {(isInstructor || isOwnComment) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  fontWeight="600"
                                  fontSize="13px"
                                  color="gray.600"
                                  _dark={{ color: "gray.400" }}
                                  _hover={{
                                    color: "purple.500",
                                    bg: "purple.50",
                                    transform: "translateY(-1px)"
                                  }}
                                  px={4}
                                  borderRadius="full"
                                  onClick={() => setReplyingTo(replyingTo === commentItem._id ? null : commentItem._id)}
                                  transition="all 0.2s ease"
                                >
                                  Reply
                                </Button>
                              )}
                            </HStack>

                            {/* Enhanced Reply input */}
                            {replyingTo === commentItem._id && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Box mt={4} pl={4}>
                                  <HStack spacing={3}>
                                    <Avatar
                                      size="sm"
                                      name={user?.username}
                                      src={user?.avatar}
                                      border="2px solid"
                                      borderColor="purple.200"
                                    />
                                    <Input
                                      placeholder="Write a reply..."
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      size="md"
                                      fontSize="14px"
                                      borderRadius="xl"
                                      border="2px solid transparent"
                                      _focus={{
                                        borderColor: "purple.400",
                                        boxShadow: "0 0 0 3px rgba(128,90,213,0.1)"
                                      }}
                                      _hover={{
                                        borderColor: "purple.200"
                                      }}
                                      bg="white"
                                      _dark={{ bg: "gray.700" }}
                                    />
                                    <IconButton
                                      icon={<FiSend />}
                                      size="md"
                                      bgGradient="linear(to-r, purple.400, blue.400)"
                                      color="white"
                                      _hover={{
                                        bgGradient: "linear(to-r, purple.500, blue.500)",
                                        transform: "scale(1.05)"
                                      }}
                                      onClick={() => handleReplySubmit(commentItem._id)}
                                      isDisabled={!replyText.trim()}
                                      borderRadius="xl"
                                      transition="all 0.2s ease"
                                    />
                                  </HStack>
                                </Box>
                              </motion.div>
                            )}

                            {/* Enhanced Display replies */}
                            {commentItem.replies && commentItem.replies.length > 0 && (
                              <VStack align="stretch" spacing={3} mt={3}>
                                {commentItem.replies.map((reply, replyIndex) => (
                                  <CommentReply
                                    key={replyIndex}
                                    reply={reply}
                                    isInstructor={reply.isInstructor}
                                  />
                                ))}
                              </VStack>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    </motion.div>
                  );
                })
              )}
            </VStack>
          </motion.div>
        </VStack>
      </Box>
    </motion.div>
  );
};


const CourseDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const { isOpen } = useSidebar();

  // Enhanced color palette with modern gradients
  const primaryColor = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  const primarySolid = '#667eea';
  const secondaryColor = '#2D3748';
  const bgColor = useColorModeValue(
    'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
    'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)'
  );
  const cardBg = useColorModeValue(
    'rgba(255,255,255,0.8)',
    'rgba(45,55,72,0.8)'
  );
  const textColor = useColorModeValue('#2D3748', 'white');
  const borderColor = useColorModeValue('rgba(255,255,255,0.2)', 'rgba(255,255,255,0.1)');

  const contentMarginLeft = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });
  const isMobile = useBreakpointValue({ base: true, md: false });

  // State management (keeping all original logic intact)
  const [courseData, setCourseData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentPart, setCurrentPart] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [courseProgress, setCourseProgress] = useState(0);
  const [completedVideos, setCompletedVideos] = useState(new Set());
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  // State for user comment filtering
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userComments, setUserComments] = useState([]);
  const [isLoadingUserComments, setIsLoadingUserComments] = useState(false);
  const [showUserComments, setShowUserComments] = useState(false);

  // New state for toggleable chapters and parts
  const [expandedChapters, setExpandedChapters] = useState(new Set([0])); // First chapter expanded by default
  const [expandedParts, setExpandedParts] = useState(new Set(['0-0'])); // First part of first chapter expanded

  // Track session
  useTrackSession(selectedVideo);

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  useEffect(() => {
    if (courseData && courseData.chapitres && courseData.chapitres.length > 0) {
      // Auto-select first available video
      const firstChapter = courseData.chapitres.find(ch => ch.isOpen);
      if (firstChapter && firstChapter.parties && firstChapter.parties.length > 0) {
        const firstPart = firstChapter.parties[0];
        if (firstPart.videos && firstPart.videos.length > 0) {
          setSelectedVideo({
            ...firstPart.videos[0],
            chapterIndex: courseData.chapitres.indexOf(firstChapter),
            partIndex: 0,
            videoIndex: 0
          });
        }
      }
    }
  }, [courseData]);

  // Fetch comments when selected video changes
  useEffect(() => {
    if (selectedVideo && selectedVideo._id && courseData) {
      fetchVideoComments(selectedVideo._id);
    }
  }, [selectedVideo, courseData]);

  const fetchCourseData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchCourseById(id);
      setCourseData(response.data);

      // Calculate course progress
      const totalVideos = response.data.chapitres?.reduce((acc, ch) =>
        acc + (ch.parties?.reduce((pAcc, p) => pAcc + p.videos.length, 0) || 0), 0) || 0;
      const completed = Math.floor(Math.random() * totalVideos * 0.7); // Mock completed videos
      setCourseProgress(totalVideos > 0 ? (completed / totalVideos) * 100 : 0);

    } catch (error) {
      console.error('Error fetching course:', error);
      toast({
        title: 'خطأ في تحميل الدورة',
        description: 'فشل في تحميل بيانات الدورة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch comments for a specific video
  const fetchVideoComments = async (videoId) => {
    if (!videoId || !courseData || !user) return;

    try {
      setIsLoadingComments(true);
      let url = user.role == "instructor" ? "/video-comments/instructor" : "/video-comments/my"
      const response = await axiosInstance.get(url, {
        params: {
          course: courseData._id
        }
      });

      if (response.data && response.data.comments) {
        // Filter comments for the current video
        const videoComments = response.data.comments.filter(comment =>
          comment.videoId === videoId
        );
        setComments(videoComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Fetch comments for a specific user
  const fetchUserComments = async (userId, videoId = null) => {
    if (!userId || !courseData) return;

    try {
      setIsLoadingUserComments(true);

      const options = {
        course: courseData._id,
        ...(videoId && { video: videoId })
      };

      const response = await getUserComments(userId, options);

      if (response && response.comments) {
        setUserComments(response.comments);
      } else {
        setUserComments([]);
      }
    } catch (error) {
      console.error('Error fetching user comments:', error);
      setUserComments([]);
      toast({
        title: 'Error',
        description: 'Failed to fetch user comments',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingUserComments(false);
    }
  };

  // Handle user selection for comment filtering
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setShowUserComments(true);
    fetchUserComments(userId, selectedVideo?._id);
  };

  const handleVideoSelect = (video, chapterIndex, partIndex, videoIndex) => {
    setSelectedVideo({
      ...video,
      chapterIndex,
      partIndex,
      videoIndex
    });
    setCurrentChapter(chapterIndex);
    setCurrentPart(partIndex);

    // Deep link attempt (only if video has a URL, is responsive, and NOT on mobile)
    if (video?.url && !isMobile) {
      (async () => {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
          const videoUrl = video.url;
          const fullVideoUrl = `${baseUrl}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;

          const response = await fetch(fullVideoUrl, { method: 'GET' });
          if (response.ok) {
            const deepLink = `cobra-edu://open?url=${fullVideoUrl}`;
            window.location.href = deepLink;
          }
        } catch (e) {
          console.warn('Video URL validation failed:', e);
        }
      })();
    }

    // Comments will be fetched automatically via useEffect
  };

  const handleNextVideo = () => {
    if (!courseData || !selectedVideo) return;

    const currentChapterData = courseData.chapitres[selectedVideo.chapterIndex];
    const currentPartData = currentChapterData.parties[selectedVideo.partIndex];

    // Try next video in current part
    if (selectedVideo.videoIndex < currentPartData.videos.length - 1) {
      const nextVideo = currentPartData.videos[selectedVideo.videoIndex + 1];
      handleVideoSelect(nextVideo, selectedVideo.chapterIndex, selectedVideo.partIndex, selectedVideo.videoIndex + 1);
      return;
    }

    // Try next part in current chapter
    if (selectedVideo.partIndex < currentChapterData.parties.length - 1) {
      const nextPart = currentChapterData.parties[selectedVideo.partIndex + 1];
      if (nextPart.videos.length > 0) {
        handleVideoSelect(nextPart.videos[0], selectedVideo.chapterIndex, selectedVideo.partIndex + 1, 0);
        return;
      }
    }

    // Try next chapter
    if (selectedVideo.chapterIndex < courseData.chapitres.length - 1) {
      const nextChapter = courseData.chapitres[selectedVideo.chapterIndex + 1];
      if (nextChapter.isOpen && nextChapter.parties.length > 0 && nextChapter.parties[0].videos.length > 0) {
        handleVideoSelect(nextChapter.parties[0].videos[0], selectedVideo.chapterIndex + 1, 0, 0);
      }
    }
  };

  const handlePreviousVideo = () => {
    if (!courseData || !selectedVideo) return;

    // Try previous video in current part
    if (selectedVideo.videoIndex > 0) {
      const currentPartData = courseData.chapitres[selectedVideo.chapterIndex].parties[selectedVideo.partIndex];
      const prevVideo = currentPartData.videos[selectedVideo.videoIndex - 1];
      handleVideoSelect(prevVideo, selectedVideo.chapterIndex, selectedVideo.partIndex, selectedVideo.videoIndex - 1);
      return;
    }

    // Try previous part in current chapter
    if (selectedVideo.partIndex > 0) {
      const currentChapterData = courseData.chapitres[selectedVideo.chapterIndex];
      const prevPart = currentChapterData.parties[selectedVideo.partIndex - 1];
      if (prevPart.videos.length > 0) {
        handleVideoSelect(prevPart.videos[prevPart.videos.length - 1], selectedVideo.chapterIndex, selectedVideo.partIndex - 1, prevPart.videos.length - 1);
        return;
      }
    }

    // Try previous chapter
    if (selectedVideo.chapterIndex > 0) {
      const prevChapter = courseData.chapitres[selectedVideo.chapterIndex - 1];
      if (prevChapter.isOpen && prevChapter.parties.length > 0) {
        const lastPart = prevChapter.parties[prevChapter.parties.length - 1];
        if (lastPart.videos.length > 0) {
          handleVideoSelect(lastPart.videos[lastPart.videos.length - 1], selectedVideo.chapterIndex - 1, prevChapter.parties.length - 1, lastPart.videos.length - 1);
        }
      }
    }
  };

  // Submit comment using the new controller format
  const handleCommentSubmit = async () => {
    if (!comment.trim() || !selectedVideo || !courseData || !user) return;

    try {
      const currentChapter = courseData.chapitres[selectedVideo.chapterIndex];
      const currentPart = currentChapter.parties[selectedVideo.partIndex];

      const commentData = {
        videoId: selectedVideo._id,
        courseId: courseData._id,
        chapterId: currentChapter._id,
        partieId: currentPart._id,
        comment: comment,
        type: 'general', // Default type
        priority: 'medium', // Default priority
        videoTitle: selectedVideo.nomvideo || selectedVideo.title,
        videoUrl: selectedVideo.url
      };

      const response = await axiosInstance.post(`/video-comments`, commentData);

      if (response.data) {
        // Add the new comment to local state
        const newComment = {
          ...response.data,
          user: {
            name: user.name || user.username,
            _id: user._id
          }
        };

        setComments(prevComments => [newComment, ...prevComments]);
        setComment('');

        toast({
          title: 'تم إرسال التعليق',
          description: 'تم إضافة تعليقك بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: 'خطأ في إرسال التعليق',
        description: error.response?.data?.message || 'فشل في إرسال التعليق، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Submit reply using the backend schema
  const handleReplySubmit = async (commentId, replyMessage) => {
    if (!replyMessage.trim() || !user) return;

    try {
      const isInstructor = user.role === 'instructor' ||
        user.username === courseData.teacherName ||
        user._id === courseData.teacherId;
      console.log(replyMessage)
      const response = await axiosInstance.post(`/video-comments/${commentId}/reply`, {
        comment: replyMessage,
        senderId: user._id,
        sender: user.username || user.name,
        isInstructor: isInstructor
      });

      if (response.data && response.data.success) {
        // Update the comment with the new reply
        setComments(prevComments =>
          prevComments.map(comment =>
            comment._id === commentId
              ? {
                ...comment,
                replies: [...(comment.replies || []), response.data.reply],
                status: isInstructor ? 'replied' : comment.status
              }
              : comment
          )
        );

        toast({
          title: 'تم إرسال الرد',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast({
        title: 'خطأ في إرسال الرد',
        description: 'فشل في إرسال الرد، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterIndex) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterIndex)) {
      newExpanded.delete(chapterIndex);
    } else {
      newExpanded.add(chapterIndex);
    }
    setExpandedChapters(newExpanded);
  };

  // Toggle part expansion
  const togglePart = (chapterIndex, partIndex) => {
    const partKey = `${chapterIndex}-${partIndex}`;
    const newExpanded = new Set(expandedParts);
    if (newExpanded.has(partKey)) {
      newExpanded.delete(partKey);
    } else {
      newExpanded.add(partKey);
    }
    setExpandedParts(newExpanded);
  };

  const getTotalVideos = () => {
    return courseData?.chapitres?.reduce((acc, ch) =>
      acc + (ch.parties?.reduce((pAcc, p) => pAcc + p.videos.length, 0) || 0), 0) || 0;
  };

  const getCurrentVideoNumber = () => {
    if (!selectedVideo || !courseData) return 1;

    let count = 1;
    for (let i = 0; i < selectedVideo.chapterIndex; i++) {
      const chapter = courseData.chapitres[i];
      count += chapter.parties?.reduce((acc, p) => acc + p.videos.length, 0) || 0;
    }

    for (let i = 0; i < selectedVideo.partIndex; i++) {
      const part = courseData.chapitres[selectedVideo.chapterIndex].parties[i];
      count += part.videos.length;
    }

    count += selectedVideo.videoIndex;
    return count;
  };

  // Calculate total duration from server data
  const getTotalDuration = () => {
    if (!courseData?.chapitres) return 'غير محدد';

    let totalMinutes = 0;
    courseData.chapitres.forEach(chapter => {
      chapter.parties?.forEach(part => {
        part.videos?.forEach(video => {
          // Assuming duration is in format "MM:SS" or just minutes
          if (video.duration) {
            const duration = video.duration.toString();
            if (duration.includes(':')) {
              const [minutes, seconds] = duration.split(':').map(Number);
              totalMinutes += minutes + (seconds / 60);
            } else {
              totalMinutes += parseInt(duration) || 0;
            }
          }
        });
      });
    });

    if (totalMinutes === 0) return courseData.duration || 'غير محدد';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);

    if (hours > 0) {
      return `${hours} ساعة و ${minutes} دقيقة`;
    }
    return `${minutes} دقيقة`;
  };


  // Enhanced Loading State
  if (isLoading) {
    return (
      <Box dir="rtl" fontFamily="Cairo, sans-serif">
        <Flex h="100vh" overflowX="hidden">
          <SidebarInstructor />
          <Box
            flex="1"
            ml={contentMarginLeft}
            p={6}
            background={bgColor}
            transition="margin-left 0.3s ease"
          >
            <Container maxW="8xl">
              <VStack spacing={8} align="stretch">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Skeleton
                    height="80px"
                    borderRadius="2xl"
                    startColor="purple.100"
                    endColor="blue.100"
                  />
                </motion.div>
                <Flex direction={{ base: 'column', lg: 'row' }} gap={8}>
                  <Box w={{ base: 'full', lg: '350px' }} flexShrink={0}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.7, delay: 0.2 }}
                    >
                      <Skeleton
                        height="400px"
                        borderRadius="2xl"
                        startColor="purple.100"
                        endColor="blue.100"
                      />
                    </motion.div>
                  </Box>
                  <Box flex="1">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.7, delay: 0.4 }}
                    >
                      <Skeleton
                        height="500px"
                        borderRadius="2xl"
                        startColor="purple.100"
                        endColor="blue.100"
                        mb={6}
                      />
                      <SkeletonText
                        mt={4}
                        noOfLines={6}
                        spacing={4}
                        startColor="purple.100"
                        endColor="blue.100"
                      />
                    </motion.div>
                  </Box>
                </Flex>
              </VStack>
            </Container>
          </Box>
        </Flex>
      </Box>
    );
  }

  // Enhanced Error State
  if (!courseData) {
    return (
      <Box dir="rtl" fontFamily="Cairo, sans-serif">
        <Flex h="100vh" overflowX="hidden">
          <SidebarInstructor />
          <Box
            flex="1"
            ml={contentMarginLeft}
            p={6}
            background={bgColor}
            transition="margin-left 0.3s ease"
          >
            <Center h="60vh">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <VStack spacing={6}>
                  <Box
                    p={6}
                    borderRadius="full"
                    bgGradient="linear(to-r, red.100, pink.100)"
                  >
                    <Icon as={FiBook} w={20} h={20} color="red.400" />
                  </Box>
                  <Heading size="xl" color="gray.600" textAlign="center">
                    الدورة غير موجودة
                  </Heading>
                  <Text color="gray.500" fontSize="lg" textAlign="center">
                    لم يتم العثور على الدورة المطلوبة
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/courses"
                    bgGradient="linear(to-r, purple.400, blue.400)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, purple.500, blue.500)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(128,90,213,0.3)"
                    }}
                    rightIcon={<FiArrowRight />}
                    size="lg"
                    borderRadius="full"
                    px={8}
                    transition="all 0.3s ease"
                  >
                    العودة للدورات
                  </Button>
                </VStack>
              </motion.div>
            </Center>
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      <Flex h="100vh" overflowX="hidden">
        <SidebarInstructor />

        <Box
          flex="1"
          ml={contentMarginLeft}
          background={bgColor}
          transition="margin-left 0.3s ease"
          overflowY="auto"
          position="relative"
        >
          {/* Enhanced Header Section with Animated Background */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Box
              bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
              color="white"
              py={12}
              px={6}
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                animation: 'float 6s ease-in-out infinite'
              }}
            >
              <Container maxW="8xl" position="relative" zIndex={1}>
                <VStack spacing={8} align="stretch">
                  {/* Enhanced Breadcrumb */}
                  <motion.div variants={slideIn}>
                    <HStack spacing={3} fontSize="sm" opacity="0.9">
                      <Text
                        _hover={{
                          color: "yellow.200",
                          cursor: 'pointer',
                          transform: "translateX(-2px)"
                        }}
                        transition="all 0.2s ease"
                      >
                        الدورات
                      </Text>
                      <Icon as={FiChevronRight} />
                      <Text color="yellow.200" fontWeight="600">
                        {courseData.name}
                      </Text>
                    </HStack>
                  </motion.div>

                  {/* Enhanced Course Title and Info */}
                  <motion.div variants={staggerContainer}>
                    <VStack align="flex-start" spacing={6}>
                      <motion.div variants={fadeIn}>
                        <HStack spacing={4} align="center" flexWrap="wrap">
                          <Heading
                            as="h1"
                            size="3xl"
                            fontWeight="900"
                            lineHeight="1.1"
                            bgGradient="linear(to-r, white, yellow.200)"
                            bgClip="text"
                            textShadow="0 2px 4px rgba(0,0,0,0.1)"
                          >
                            {courseData.name}
                          </Heading>
                          <Badge
                            bgGradient="linear(to-r, yellow.400, orange.400)"
                            color="white"
                            px={4}
                            py={2}
                            borderRadius="full"
                            fontSize="md"
                            fontWeight="700"
                            boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                            _hover={{
                              transform: "scale(1.05)",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.3)"
                            }}
                            transition="all 0.2s ease"
                          >
                            {courseData.category}
                          </Badge>
                        </HStack>
                      </motion.div>

                      <motion.div variants={fadeIn}>
                        <HStack spacing={6} flexWrap="wrap">
                          <HStack spacing={3}>
                            <Avatar
                              src="/assets/oussama-loussifi.jpeg"
                              size="md"
                              border="3px solid"
                              borderColor="rgba(255,255,255,0.3)"
                              boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                            />
                            <VStack align="flex-start" spacing={1}>
                              <Text fontWeight="700" fontSize="lg" color="white">
                                {courseData.teacherName}
                              </Text>
                              <Text fontSize="sm" opacity="0.8" color="yellow.100">
                                أستاذ
                              </Text>
                            </VStack>
                          </HStack>

                          <Box h="50px" w="1px" bg="rgba(255,255,255,0.3)" />

                          <HStack spacing={6} flexWrap="wrap">
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiUsers} boxSize={5} />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {courseData.enrolledStudents || 0}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    طالب
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiClock} boxSize={5} />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {getTotalDuration()}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    مدة الدورة
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>

                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                            >
                              <HStack spacing={2}>
                                <Box
                                  p={2}
                                  borderRadius="lg"
                                  bg="rgba(255,255,255,0.1)"
                                  backdropFilter="blur(10px)"
                                >
                                  <Icon as={FiStar} boxSize={5} color="yellow.300" />
                                </Box>
                                <VStack align="flex-start" spacing={0}>
                                  <Text fontSize="lg" fontWeight="700">
                                    {courseData.rating || 4.8}
                                  </Text>
                                  <Text fontSize="xs" opacity="0.8">
                                    تقييم
                                  </Text>
                                </VStack>
                              </HStack>
                            </motion.div>
                          </HStack>
                        </HStack>
                      </motion.div>
                    </VStack>
                  </motion.div>
                </VStack>
              </Container>
            </Box>
          </motion.div>

          {/* Enhanced Main Content */}
          <Container maxW="8xl" py={10}>
            <Flex spacing={10} gap={10} direction={{ base: 'column', lg: 'row' }}>
              {/* Enhanced Course Curriculum Sidebar */}
              <motion.div
                variants={slideIn}
                initial="hidden"
                animate="visible"
              >
                <Box w={{ base: 'full', lg: '380px' }} flexShrink={0}>
                  <Box
                    bg={cardBg}
                    borderRadius="3xl"
                    boxShadow="0 20px 40px rgba(0,0,0,0.1)"
                    position="sticky"
                    top="20px"
                    border="1px solid"
                    borderColor={borderColor}
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                  >
                    <Box
                      bgGradient="linear(to-r, purple.400, blue.400)"
                      color="white"
                      p={6}
                    >
                      <Heading size="lg" fontWeight="700" mb={2}>
                        محتوى الدورة
                      </Heading>
                      <Text fontSize="sm" opacity="0.9">
                        {getTotalVideos()} فيديو • {getTotalDuration()}
                      </Text>
                    </Box>

                    <Box p={6}>
                      <VStack spacing={4} align="stretch" maxH="600px" overflowY="auto">
                        {courseData.chapitres?.map((chapter, chapterIndex) => {
                          const colorScheme = getChapterColorScheme();
                          const isChapterExpanded = expandedChapters.has(chapterIndex);

                          return (
                            <motion.div
                              key={chapterIndex}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: chapterIndex * 0.1 }}
                            >
                              <Box>
                                {/* Enhanced Chapter Header */}
                                <Box
                                  as="button"
                                  w="full"
                                  bgGradient={colorScheme.chapter}
                                  borderRadius="xl"
                                  _hover={{
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 20px rgba(102,126,234,0.3)"
                                  }}
                                  px={5}
                                  py={4}
                                  onClick={() => toggleChapter(chapterIndex)}
                                  transition="all 0.3s ease"
                                  boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                                  mb={3}
                                  color="white"
                                >
                                  <HStack justify="space-between" w="full">
                                    <VStack align="flex-start" spacing={1}>
                                      <Text fontWeight="700" fontSize="md" textAlign="right">
                                        {chapter.nom}
                                      </Text>
                                      <Text fontSize="xs" opacity="0.9">
                                        {chapter.parties?.reduce((acc, p) => acc + p.videos.length, 0) || 0} فيديو
                                      </Text>
                                    </VStack>
                                    <HStack spacing={3}>
                                      {chapter.isOpen ? (
                                        <Icon as={FiUnlock} color="green.200" boxSize={5} />
                                      ) : (
                                        <Icon as={FiLock} color="red.200" boxSize={5} />
                                      )}
                                      <Icon
                                        as={isChapterExpanded ? FiChevronUp : FiChevronDown}
                                        boxSize={5}
                                        transition="transform 0.3s ease"
                                        transform={isChapterExpanded ? "rotate(180deg)" : "rotate(0deg)"}
                                      />
                                    </HStack>
                                  </HStack>
                                </Box>

                                {/* Enhanced Chapter Content */}
                                <Collapse in={isChapterExpanded} animateOpacity>
                                  {chapter.isOpen && (
                                    <Box mb={4} pl={3}>
                                      <VStack spacing={3} align="stretch">
                                        {chapter.parties?.map((part, partIndex) => {
                                          const partKey = `${chapterIndex}-${partIndex}`;
                                          const isPartExpanded = expandedParts.has(partKey);

                                          return (
                                            <motion.div
                                              key={partIndex}
                                              initial={{ opacity: 0, x: -20 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              transition={{ duration: 0.3, delay: partIndex * 0.05 }}
                                            >
                                              <Box>
                                                {/* Enhanced Part Header */}
                                                <Box
                                                  as="button"
                                                  w="full"
                                                  bgGradient={colorScheme.part}
                                                  borderRadius="lg"
                                                  _hover={{
                                                    transform: "translateY(-1px)",
                                                    boxShadow: "0 6px 16px rgba(240,147,251,0.2)"
                                                  }}
                                                  px={4}
                                                  py={3}
                                                  onClick={() => togglePart(chapterIndex, partIndex)}
                                                  transition="all 0.2s ease"
                                                  boxShadow="0 2px 8px rgba(0,0,0,0.05)"
                                                  mb={2}
                                                  color="white"
                                                >
                                                  <HStack justify="space-between" w="full">
                                                    <Text
                                                      fontSize="sm"
                                                      fontWeight="600"
                                                      textAlign="right"
                                                    >
                                                      {part.name}
                                                    </Text>
                                                    <Icon
                                                      as={isPartExpanded ? FiChevronUp : FiChevronDown}
                                                      boxSize={4}
                                                      transition="transform 0.2s ease"
                                                    />
                                                  </HStack>
                                                </Box>

                                                {/* Enhanced Part Videos */}
                                                <Collapse in={isPartExpanded} animateOpacity>
                                                  <VStack spacing={2} align="stretch" pl={3} mt={2}>
                                                    {part.videos?.map((video, videoIndex) => {
                                                      const isSelected = selectedVideo &&
                                                        selectedVideo.chapterIndex === chapterIndex &&
                                                        selectedVideo.partIndex === partIndex &&
                                                        selectedVideo.videoIndex === videoIndex;

                                                      return (
                                                        <motion.div
                                                          key={videoIndex}
                                                          whileHover={{ scale: 1.02 }}
                                                          whileTap={{ scale: 0.98 }}
                                                        >
                                                          <Box
                                                            as="button"
                                                            w="full"
                                                            p={4}
                                                            bg={isSelected ?
                                                              "linear-gradient(135deg, rgba(102,126,234,0.2) 0%, rgba(118,75,162,0.2) 100%)" :
                                                              "rgba(255,255,255,0.5)"
                                                            }
                                                            _dark={{
                                                              bg: isSelected ?
                                                                "linear-gradient(135deg, rgba(102,126,234,0.3) 0%, rgba(118,75,162,0.3) 100%)" :
                                                                "rgba(45,55,72,0.5)"
                                                            }}
                                                            borderRadius="lg"
                                                            _hover={{
                                                              bg: isSelected ?
                                                                "linear-gradient(135deg, rgba(102,126,234,0.3) 0%, rgba(118,75,162,0.3) 100%)" :
                                                                "rgba(255,255,255,0.8)",
                                                              transform: "translateY(-1px)",
                                                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                                                            }}
                                                            onClick={() => handleVideoSelect(video, chapterIndex, partIndex, videoIndex)}
                                                            textAlign="right"
                                                            border={isSelected ? `2px solid ${colorScheme.chapterSolid}` : '2px solid transparent'}
                                                            transition="all 0.2s ease"
                                                            boxShadow={isSelected ? "0 4px 12px rgba(102,126,234,0.2)" : "0 2px 4px rgba(0,0,0,0.05)"}
                                                          >
                                                            <HStack spacing={4}>
                                                              <Box
                                                                w="12px"
                                                                h="12px"
                                                                borderRadius="full"
                                                                bg={isSelected ? colorScheme.chapterSolid : 'gray.400'}
                                                                flexShrink={0}
                                                                boxShadow={isSelected ? "0 0 8px rgba(102,126,234,0.5)" : "none"}
                                                              />

                                                              <VStack align="flex-start" spacing={2} flex="1">
                                                                <Text
                                                                  fontSize="sm"
                                                                  fontWeight={isSelected ? "700" : "600"}
                                                                  color={isSelected ? colorScheme.chapterSolid : colorScheme.text}
                                                                  textAlign="right"
                                                                  noOfLines={2}
                                                                  lineHeight="1.4"
                                                                >
                                                                  {video.nomvideo}
                                                                </Text>

                                                                <HStack spacing={3} fontSize="xs" color="gray.600">
                                                                  <HStack spacing={1}>
                                                                    <Icon as={FiClock} />
                                                                    <Text fontWeight="500">{video.duree || "5:30"}</Text>
                                                                  </HStack>
                                                                  {completedVideos.has(`${chapterIndex}-${partIndex}-${videoIndex}`) && (
                                                                    <HStack spacing={1} color="green.500">
                                                                      <Icon as={FiCheckCircle} />
                                                                      <Text fontWeight="600">مكتمل</Text>
                                                                    </HStack>
                                                                  )}
                                                                </HStack>
                                                              </VStack>

                                                              {isSelected && (
                                                                <motion.div
                                                                  initial={{ scale: 0 }}
                                                                  animate={{ scale: 1 }}
                                                                  transition={{ duration: 0.2 }}
                                                                >
                                                                  <Icon
                                                                    as={FiPlay}
                                                                    color={colorScheme.chapterSolid}
                                                                    boxSize={5}
                                                                  />
                                                                </motion.div>
                                                              )}
                                                            </HStack>
                                                          </Box>
                                                        </motion.div>
                                                      );
                                                    })}
                                                  </VStack>
                                                </Collapse>
                                              </Box>
                                            </motion.div>
                                          );
                                        })}
                                      </VStack>
                                    </Box>
                                  )}
                                </Collapse>
                              </Box>
                            </motion.div>
                          );
                        })}
                      </VStack>
                    </Box>
                  </Box>
                </Box>
              </motion.div>


              {/* Enhanced Main Video Content */}
              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate="visible"
                style={{ flex: 1 }}
              >
                <VStack spacing={8} align="stretch">
                  {/* Enhanced Video Player Section */}
                  <Box>
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        bg="linear-gradient(135deg, #000000 0%, #1a1a1a 100%)"
                        borderRadius="2xl"
                        overflow="hidden"
                        position="relative"
                        mb={6}
                        boxShadow="0 20px 40px rgba(0,0,0,0.3)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.1)"
                        _before={{
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: '2xl',
                          padding: '2px',
                          background: 'linear-gradient(135deg, rgba(102,126,234,0.3), rgba(118,75,162,0.3))',
                          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          maskComposite: 'exclude',
                          zIndex: -1
                        }}
                      >
                        <AspectRatio ratio={16 / 9}>
                          {selectedVideo ? (
                            <VideoPlayer
                              src={selectedVideo.url}
                              poster={selectedVideo.thumbnail}
                              idvdocipher={selectedVideo.idvdocipher}
                              videoId={selectedVideo.idvdocipher}
                              video={selectedVideo}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "2xl"
                              }}
                            />
                          ) : (
                            <Center bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)" color="white">
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                              >
                                <VStack spacing={6}>
                                  <Box
                                    p={6}
                                    borderRadius="full"
                                    bgGradient="linear(to-r, purple.400, blue.400)"
                                    boxShadow="0 8px 20px rgba(102,126,234,0.3)"
                                  >
                                    <Icon as={FiPlay} boxSize={20} />
                                  </Box>
                                  <Text fontSize="xl" fontWeight="600">
                                    اختر فيديو لبدء التعلم
                                  </Text>
                                </VStack>
                              </motion.div>
                            </Center>
                          )}
                        </AspectRatio>
                      </Box>
                    </motion.div>

                    {/* Enhanced Video Information */}
                    {selectedVideo && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <VStack align="stretch" spacing={6}>
                          <Box
                            p={6}
                            bg={cardBg}
                            borderRadius="2xl"
                            boxShadow="0 10px 25px rgba(0,0,0,0.1)"
                            border="1px solid"
                            borderColor={borderColor}
                            backdropFilter="blur(20px)"
                          >
                            <VStack align="stretch" spacing={4}>
                              <Heading
                                size="xl"
                                fontWeight="700"
                                lineHeight="1.3"
                                color={textColor}
                                bgGradient="linear(to-r, purple.600, blue.600)"
                                bgClip="text"
                              >
                                {selectedVideo.title || selectedVideo.nomvideo}
                              </Heading>

                              {/* Enhanced Video Navigation */}
                              <HStack justify="space-between" align="center" flexWrap="wrap">
                                <HStack spacing={3}>
                                  <Button
                                    leftIcon={<FiChevronRight />}
                                    variant="outline"
                                    size="md"
                                    onClick={handlePreviousVideo}
                                    borderRadius="full"
                                    borderColor="purple.200"
                                    color="purple.600"
                                    _hover={{
                                      bg: "purple.50",
                                      borderColor: "purple.400",
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 4px 12px rgba(128,90,213,0.2)"
                                    }}
                                    _dark={{
                                      borderColor: "purple.400",
                                      color: "purple.300",
                                      _hover: { bg: "purple.900" }
                                    }}
                                    transition="all 0.2s ease"
                                    fontWeight="600"
                                    px={6}
                                  >
                                    السابق
                                  </Button>
                                  <Button
                                    rightIcon={<FiChevronLeft />}
                                    variant="outline"
                                    size="md"
                                    onClick={handleNextVideo}
                                    borderRadius="full"
                                    borderColor="blue.200"
                                    color="blue.600"
                                    _hover={{
                                      bg: "blue.50",
                                      borderColor: "blue.400",
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 4px 12px rgba(79,172,254,0.2)"
                                    }}
                                    _dark={{
                                      borderColor: "blue.400",
                                      color: "blue.300",
                                      _hover: { bg: "blue.900" }
                                    }}
                                    transition="all 0.2s ease"
                                    fontWeight="600"
                                    px={6}
                                  >
                                    التالي
                                  </Button>
                                </HStack>

                                <Box
                                  px={4}
                                  py={2}
                                  bgGradient="linear(to-r, purple.100, blue.100)"
                                  _dark={{ bgGradient: "linear(to-r, purple.800, blue.800)", borderColor: "purple.600" }}
                                  borderRadius="full"
                                  border="1px solid"
                                  borderColor="purple.200"
                                >
                                  <Text fontSize="sm" fontWeight="600" color="purple.700" _dark={{ color: "purple.200" }}>
                                    فيديو {getCurrentVideoNumber()} من {getTotalVideos()}
                                  </Text>
                                </Box>
                              </HStack>

                              {/* Enhanced Video Description */}
                              {selectedVideo.description && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  transition={{ duration: 0.4 }}
                                >
                                  <Box
                                    bg="linear-gradient(135deg, rgba(247,250,252,0.8) 0%, rgba(237,242,247,0.9) 100%)"
                                    _dark={{ bg: "linear-gradient(135deg, rgba(45,55,72,0.8) 0%, rgba(74,85,104,0.9) 100%)", borderColor: "purple.700" }}
                                    borderRadius="xl"
                                    p={5}
                                    border="1px solid"
                                    borderColor="purple.100"
                                    position="relative"
                                    _before={{
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      w: "4px",
                                      h: "100%",
                                      bgGradient: "linear(to-b, purple.400, blue.400)",
                                      borderRadius: "full"
                                    }}
                                  >
                                    <Text
                                      fontSize="md"
                                      lineHeight="1.7"
                                      color={textColor}
                                      pl={4}
                                    >
                                      {selectedVideo.description}
                                    </Text>
                                  </Box>
                                </motion.div>
                              )}
                            </VStack>
                          </Box>
                        </VStack>
                      </motion.div>
                    )}
                  </Box>

                  {/* Enhanced Comments Section */}
                  {isLoadingComments ? (
                    <Center py={12}>
                      <VStack spacing={4}>
                        <Box position="relative">
                          <Spinner
                            size="xl"
                            color="purple.400"
                            thickness="4px"
                          />
                          <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                          >
                            <Spinner
                              size="lg"
                              color="blue.400"
                              thickness="3px"
                            />
                          </Box>
                        </Box>
                        <Text color="gray.500" fontWeight="500">
                          جاري تحميل التعليقات...
                        </Text>
                      </VStack>
                    </Center>
                  ) : (
                    <YouTubeComments
                      comments={comments}
                      onCommentSubmit={handleCommentSubmit}
                      comment={comment}
                      setComment={setComment}
                      user={user}
                      courseData={courseData}
                      selectedVideo={selectedVideo}
                      onReplySubmit={handleReplySubmit}
                    />
                  )}
                </VStack>
              </motion.div>
            </Flex>
          </Container>
        </Box>
      </Flex>

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(1deg);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(102,126,234,0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(102,126,234,0.5);
          }
        }
        
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </Box>
  );
};

// Wrapper component with context providers (keeping original structure)
const CourseDetailsInstructor = () => {
  return (
    <SidebarProvider>
      <CourseDetailsContent />
    </SidebarProvider>
  );
};

export default CourseDetailsInstructor;

